import { sdk } from '../sdk';

export interface AuditLog {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: 'SuperAdmin' | 'Admin' | 'Cashier';
  action: 'create' | 'update' | 'delete' | 'login' | 'logout' | 'view' | 'export' | 'print';
  entity: 'User' | 'Donor' | 'GuestDonor' | 'Donation' | 'Expense' | 'Region' | 'Office' | 'Controller' | 'Account' | 'Transaction' | 'DonationType' | 'ExpenseType';
  entityId?: string;
  entityData?: any;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  ipAddress: string;
  userAgent?: string;
  deviceInfo?: {
    pcName?: string;
    pcId?: string;
    macAddress?: string;
    platform?: string;
  };
  status: 'success' | 'failure';
  statusCode?: number;
  errorMessage?: string;
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata?: Record<string, any>;
  prevHash?: string;
  hash: string;
  timestamp: string;
  createdAt: string;
}

export interface AuditLogFilters {
  page?: number;
  limit?: number;
  action?: string;
  entity?: string;
  userRole?: string;
  status?: string;
  userEmail?: string;
  startDate?: string;
  endDate?: string;
  entityId?: string;
}

export interface AuditLogResponse {
  success: boolean;
  data: AuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface AuditLogStats {
  total: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  actionCounts: { _id: string; count: number }[];
  entityCounts: { _id: string; count: number }[];
  statusCounts: { _id: string; count: number }[];
  roleCounts: { _id: string; count: number }[];
}

export const AuditLogsAPI = {
  /**
   * Get all audit logs with filtering and pagination
   */
  async getAllAuditLogs(filters?: AuditLogFilters): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const response = await sdk.client.get(`/api/v1/audit-logs?${params.toString()}`);
    // SDK client.get() already returns res.data, so response = { success: true, data: [...], pagination: {...} }
    // Extract data and pagination from backend response structure
    const logs = response?.data || [];
    const pagination = response?.pagination || { page: 1, limit: 50, total: 0, pages: 0 };
    
    return {
      data: Array.isArray(logs) ? logs : [],
      pagination
    };
  },

  /**
   * Get a single audit log by ID
   */
  async getAuditLogById(id: string): Promise<{ success: boolean; data: AuditLog }> {
    const response = await sdk.client.get(`/api/v1/audit-logs/${id}`);
    return response.data || response;
  },

  /**
   * Get audit logs for a specific entity
   */
  async getEntityAuditLogs(
    entity: string,
    entityId?: string,
    page?: number,
    limit?: number
  ): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));
    const url = entityId
      ? `/api/v1/audit-logs/entity/${entity}/${entityId}?${params.toString()}`
      : `/api/v1/audit-logs/entity/${entity}?${params.toString()}`;
    const response = await sdk.client.get(url);
    return response.data || response;
  },

  /**
   * Get audit logs for a specific user
   */
  async getUserAuditLogs(
    userId: string,
    page?: number,
    limit?: number
  ): Promise<AuditLogResponse> {
    const params = new URLSearchParams();
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));
    const response = await sdk.client.get(`/api/v1/audit-logs/user/${userId}?${params.toString()}`);
    return response.data || response;
  },

  /**
   * Get audit log statistics
   */
  async getAuditLogStats(startDate?: string, endDate?: string): Promise<{ success: boolean; data: AuditLogStats }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await sdk.client.get(`/api/v1/audit-logs/stats?${params.toString()}`);
    return response.data || response;
  }
};
